import classNames from 'classnames'

type SectionDividerProps = {
  /** Position of the divider relative to content */
  position?: 'above' | 'below'
  /** Visual weight of the divider */
  weight?: 'light' | 'medium' | 'strong'
  /** Additional CSS classes */
  className?: string
  /** Spacing around the divider */
  spacing?: 'tight' | 'normal' | 'loose'
}

/**
 * A reusable section divider component that provides consistent styling
 * for separating content sections with visual hierarchy.
 *
 * Used in search results, filter views, and other grouped content areas.
 */
export function SectionDivider({
  weight = 'medium',
  spacing = 'normal',
  className,
}: SectionDividerProps) {
  const baseClasses = 'flex-grow'

  // Border position and thickness classes based on weight
  const thicknessClasses = weight === 'light' ? 'border-b' : 'border-b-2'

  // Weight-based color classes
  const weightClasses = {
    light: 'border-gray-200 dark:border-gray-700',
    medium: 'border-zinc-200 dark:border-zinc-800',
    strong: 'border-zinc-300 dark:border-zinc-700',
  }[weight]

  // Spacing classes
  const spacingClasses = {
    tight: 'pt-0.5',
    normal: 'pt-1',
    loose: 'pt-2',
  }[spacing]

  return (
    <div
      className={classNames(
        baseClasses,
        thicknessClasses,
        weightClasses,
        spacingClasses,
        className
      )}
    />
  )
}

/**
 * Preset for search result section dividers (above titles)
 */
export function SearchSectionDivider({ className }: { className?: string }) {
  return (
    <SectionDivider weight='medium' spacing='normal' className={className} />
  )
}

/**
 * Preset for filter view headline dividers (below titles)
 */
export function FilterHeadlineDivider({ className }: { className?: string }) {
  return (
    <SectionDivider weight='strong' spacing='tight' className={className} />
  )
}

/**
 * Preset for light item separators
 */
export function ItemSeparator({ className }: { className?: string }) {
  return <SectionDivider weight='light' spacing='tight' className={className} />
}
